if (!customElements.get("cart-items")) {
class CartItems extends HTMLElement {
  static giftNoteQueue = Promise.resolve();
  static giftNoteAttempts = new Set();
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.cartUpdateEvent =
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
      "cart-update";
    this.cartErrorEvent =
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) || "cart-error";
    this.drawer = null;
    this.page = null;
    this.pageContent = null;
    this.pageSectionId = null;
    this.checkoutButton = null;
  }

  connectedCallback() {
    this.drawer = document.getElementById("Cart-Drawer");
    this.page = document.getElementById("Cart-Page");
    this.pageSectionId = this.page?.dataset?.sectionId || null;
    this.pageContent = this.page?.querySelector("[data-cart-page-content]") || null;
    this.checkoutButton = this.drawer?.querySelector(
      ".cart-drawer__summary-checkout"
    );
    this.addEventListener("change", this.onChange);
    this.addEventListener("click", this.onClick);
    if (!window.Shopify?.designMode) {
      CartItems.giftNoteQueue = CartItems.giftNoteQueue.then(() => this.syncGiftNote());
    }
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
    this.removeEventListener("click", this.onClick);
    this.drawer = null;
    this.page = null;
    this.pageContent = null;
    this.pageSectionId = null;
    this.checkoutButton = null;
  }

  async syncGiftNote() {
    if (!this.isConnected) return;
    for (const row of this.querySelectorAll("[data-gift-note]")) {
      const token = row.dataset.giftKey + row.dataset.giftNote + row.dataset.shippingNote;
      if (CartItems.giftNoteAttempts.has(token)) continue;
      const quantity = row.querySelector('input[name="updates[]"]')?.value;
      if (!quantity) continue;
      let properties;
      let repaired = false;
      try {
        const raw = JSON.parse(row.dataset.giftProperties || "{}") || {};
        properties = Array.isArray(raw) ? Object.fromEntries(raw) : raw;
        for (const [key, value] of Object.entries(properties)) {
          if (/^\d+$/.test(key) && Array.isArray(value) && value.length === 2 && typeof value[0] === "string") {
            if (!(value[0] in properties)) properties[value[0]] = value[1];
            delete properties[key];
            repaired = true;
          }
        }
      } catch {
        continue;
      }
      const previous = JSON.stringify(properties);
      properties["_Sale type"] = properties["Sale type"] || properties["_Sale type"] || "Pre-order";
      ["Sale type", "Expected availability", "Payment", "Pre-order gift"].forEach(key => delete properties[key]);
      properties["Shipping"] = row.dataset.shippingNote;
      properties["Gift included"] = row.dataset.giftNote;
      if (!repaired && JSON.stringify(properties) === previous) continue;
      CartItems.giftNoteAttempts.add(token);
      const checkout = this.checkoutButton;
      const checkoutWasDisabled = checkout?.disabled;
      this.inert = true;
      if (checkout) checkout.disabled = true;
      try {
        await this.updateLine(row.dataset.cartLine, quantity, row.dataset.id, quantity, properties);
      } finally {
        this.inert = false;
        if (checkout?.isConnected) checkout.disabled = checkoutWasDisabled;
      }
      return;
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: "Cart-Icon-Bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      }
    ];
    const drawerSectionId = this.drawer?.dataset?.sectionId;
    if (drawerSectionId) {
      sections.push({
        id: "Cart-Drawer",
        section: drawerSectionId,
        selector: "[data-cart-drawer-content]"
      });
    }
    if (this.pageSectionId) {
      sections.push({
        id: "Cart-Page",
        section: this.pageSectionId,
        selector: "[data-cart-page-content]"
      });
    }
    return sections;
  }

  renderSections(sections = {}) {
    this.getSectionsToRender().forEach((section) => {
      const html = sections[section.section];
      if (!html) return;
      const container = document.getElementById(section.id);
      if (!container) return;
      if (!section.selector) {
        container.innerHTML = html;
        return;
      }
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const element = parsed.querySelector(section.selector);
      if (element) {
        if (section.id === "Cart-Drawer") {
          const contentTarget = container.querySelector(
            "[data-cart-drawer-content]"
          );
          const summaryTarget = container.querySelector(
            "[data-cart-drawer-summary]"
          );
          const progressTarget = container.querySelector(
            "free-shipping-progress"
          );
          if (contentTarget) {
            contentTarget.innerHTML = element.innerHTML;
            this.refreshLazyImages(contentTarget);
          }
          const summaryElement = parsed.querySelector(
            "[data-cart-drawer-summary]"
          );
          if (summaryTarget && summaryElement) {
            summaryTarget.innerHTML = summaryElement.innerHTML;
            summaryTarget.className = summaryElement.className;
          }
          const progressSource = parsed.querySelector("free-shipping-progress");
          if (progressTarget && progressSource) {
            progressTarget.dataset.totalAmount =
              progressSource.dataset.totalAmount || "";
          }
          this.drawer = container;
          this.checkoutButton = this.drawer.querySelector(
            ".cart-drawer__summary-checkout"
          );
          if (typeof container.refreshUpsell === "function") {
            container.refreshUpsell();
          }
          return;
        }
        if (section.id === "Cart-Page") {
          const contentTarget = container.querySelector(
            "[data-cart-page-content]"
          );
          const contentSource = parsed.querySelector(
            "[data-cart-page-content]"
          );
          if (contentTarget && contentSource) {
            contentTarget.innerHTML = contentSource.innerHTML;
            this.refreshLazyImages(contentTarget);
          }
          this.page = container;
          this.pageContent = contentTarget;
          return;
        }
        container.innerHTML = element.innerHTML;
        return;
      }
      container.innerHTML = html;
    });
  }

  refreshLazyImages(target) {
    if (!target) return;
    if (!window.webvista || typeof window.webvista.initLazyImageAnimation !== "function") {
      return;
    }
    window.webvista.initLazyImageAnimation(target);
  }

  // Handle quantity input changes
  onChange(event) {
    const input = event.target.closest('input[name="updates[]"]');
    if (!input) return;
    const line = input.dataset.line || input.dataset.index;
    if (!line) return;
    const quantity = parseInt(input.value, 10);
    if (Number.isNaN(quantity)) return;
    if (input.dataset.lastValue === String(quantity)) return;
    const previousQty = input.dataset.lastValue || input.defaultValue;
    input.dataset.lastValue = String(quantity);
    const variantId = input.dataset.quantityVariantId || null;
    this.updateLine(line, quantity, variantId, previousQty);
  }

  // Handle remove button clicks
  onClick(event) {
    const removeButton = event.target.closest("[data-cart-remove]");
    if (!removeButton) return;
    event.preventDefault();
    const line = removeButton.dataset.line;
    if (!line) return;
    this.updateLine(line, 0, null);
  }

  // Set loading state for a cart line item
  setLineLoading(line, isLoading) {
    const row = this.querySelector(`[data-cart-line="${line}"]`);
    if (!row) return;
    row.classList.toggle("is-loading", isLoading);
    row.classList.toggle("pointer-events-none", isLoading);
    row.classList.toggle("opacity-60", isLoading);

    if (!this.checkoutButton || !this.checkoutButton.isConnected) {
      this.drawer = this.drawer || document.getElementById("Cart-Drawer");
      this.checkoutButton = this.drawer?.querySelector(
        ".cart-drawer__summary-checkout"
      );
    }
    if (this.checkoutButton) {
      this.checkoutButton.classList.toggle("pointer-events-none", isLoading);
      this.checkoutButton.classList.toggle("opacity-60", isLoading);
    }
  }

  // Show inline error on a specific cart line item
  showLineError(line, message) {
    const row = document.querySelector(`[data-cart-line="${line}"]`);
    if (!row) return;
    const errorEl = row.querySelector("[data-cart-item-error]");
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.classList.remove("hidden");
  }

  // Clear all inline cart item errors
  clearLineErrors() {
    document.querySelectorAll("[data-cart-item-error]").forEach((el) => {
      el.textContent = "";
      el.classList.add("hidden");
    });
  }

  // Update cart line item quantity
  async updateLine(line, quantity, variantId, previousQty, properties) {
    this.setLineLoading(line, true);
    this.clearLineErrors();
    try {
      const sections = this.getSectionsToRender();
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          ...(properties ? { properties } : {}),
          sections: sections.map((section) => section.section).join(","),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message =
          errorData?.description || errorData?.message || "";
        document.dispatchEvent(
          new CustomEvent(this.cartErrorEvent, { detail: { error: errorData } })
        );

        if (response.status === 422) {
          // 422 = partial success: Shopify changed the qty to max available.
          // Fetch fresh sections to reflect the actual cart state.
          try {
            const sectionIds = this.getSectionsToRender().map((s) => s.section);
            const refreshRes = await fetch("/cart/update.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({
                sections: sectionIds,
                sections_url: window.location.pathname,
              }),
            });
            if (refreshRes.ok) {
              const refreshData = await refreshRes.json();
              if (refreshData?.sections) {
                this.renderSections(refreshData.sections);
              }
            }
          } catch (_) {
            // silent
          }
          // showLineError after re-render so new DOM has the error element
          if (message) this.showLineError(line, message);
        } else {
          // Other errors (404, 500): cart didn't change, revert input.
          if (previousQty != null) {
            const input = this.querySelector(
              `input[name="updates[]"][data-line="${line}"]`
            ) || document.querySelector(
              `input[name="updates[]"][data-line="${line}"]`
            );
            if (input) {
              input.value = previousQty;
              input.dataset.lastValue = String(previousQty);
            }
          }
          if (message) this.showLineError(line, message);
        }
        return;
      }

      const data = await response.json();
      const requestedQty = Number(quantity);

      if (data?.sections) {
        this.renderSections(data.sections);
      }

      // Check if quantity was capped by inventory
      if (requestedQty > 0 && data.items) {
        const updatedItem = data.items[Number(line) - 1];
        if (updatedItem && updatedItem.quantity < requestedQty) {
          const template =
            this.dataset.quantityError ||
            document.querySelector("cart-items[data-quantity-error]")?.dataset.quantityError ||
            "All [quantity] are in your cart.";
          const message = template.replace(
            "[quantity]",
            String(updatedItem.quantity)
          );
          this.showLineError(line, message);
        }
      }

      document.dispatchEvent(
        new CustomEvent(this.cartUpdateEvent, {
          detail: { cart: data, variantId: variantId }
        })
      );
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent(this.cartErrorEvent, { detail: { error } })
      );
    } finally {
      this.setLineLoading(line, false);
    }
  }
}

customElements.define("cart-items", CartItems);
}
